import { useCallback, useEffect, useState } from "react";
import {
    createMedicalEquipmentSupplier,
    deleteMedicalEquipmentSupplier,
    listMedicalEquipmentSuppliers,
    updateMedicalEquipmentSupplier,
} from "../../../services/api";
import { emptySupplier } from "../model/materielConstants";

export function supplierEmails(supplier) {
    return supplier.emails?.length ? supplier.emails : [supplier.email].filter(Boolean);
}

function supplierToForm(supplier) {
    return {
        name: supplier.name || "",
        email: supplier.email || "",
        emails: supplierEmails(supplier),
        phone: supplier.phone || "",
        address: supplier.address || "",
        contactName: supplier.contactName || "",
        notes: supplier.notes || "",
    };
}

// Champs texte nettoyés ; le mail principal est le premier de la liste.
function supplierPayload(form) {
    const payload = Object.fromEntries(
        Object.entries(form).map(([key, value]) => [key, typeof value === "string" ? value.trim() : value])
    );
    payload.emails = form.emails;
    payload.email = form.emails[0] || "";
    return payload;
}

// Fournisseurs d'un service : liste triée par nom, formulaire de création ou de modification, suppression.
export function useSuppliers(serviceKey) {
    const [suppliers, setSuppliers] = useState([]);
    const [form, setForm] = useState(emptySupplier);
    const [editingId, setEditingId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const refresh = useCallback(() => {
        setLoading(true);
        setError(null);
        listMedicalEquipmentSuppliers(serviceKey)
            .then(response => setSuppliers(response.data || []))
            .catch(() => setError("Impossible de charger les fournisseurs."))
            .finally(() => setLoading(false));
    }, [serviceKey]);

    useEffect(() => { refresh(); }, [refresh]);

    const resetForm = () => {
        setForm(emptySupplier);
        setEditingId(null);
    };

    const submit = async (event) => {
        event.preventDefault();
        setSaving(true);
        setError(null);
        const payload = supplierPayload(form);
        try {
            const response = editingId
                ? await updateMedicalEquipmentSupplier(serviceKey, editingId, payload)
                : await createMedicalEquipmentSupplier(serviceKey, payload);
            setSuppliers(prev => editingId
                ? prev.map(item => item.id === editingId ? response.data : item)
                : [...prev, response.data].sort((a, b) => a.name.localeCompare(b.name, "fr")));
            resetForm();
        } catch (err) {
            setError(err.response?.data?.message || "Impossible d'enregistrer ce fournisseur.");
        } finally {
            setSaving(false);
        }
    };

    const edit = (supplier) => {
        setEditingId(supplier.id);
        setForm(supplierToForm(supplier));
    };

    const remove = async (supplier) => {
        if (!globalThis.confirm(`Supprimer ${supplier.name} ?`)) return;
        setError(null);
        try {
            await deleteMedicalEquipmentSupplier(serviceKey, supplier.id);
            setSuppliers(prev => prev.filter(item => item.id !== supplier.id));
            if (editingId === supplier.id) resetForm();
        } catch (err) {
            setError(err.response?.data?.message || "Impossible de supprimer ce fournisseur.");
        }
    };

    return { suppliers, form, setForm, editingId, loading, saving, error, refresh, resetForm, submit, edit, remove };
}
